import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  LogOut, Users, ClipboardList, Shield, Clock, Loader2,
  LayoutDashboard, BookOpen, Calendar, Newspaper, GitBranch,
  UserCircle, Kanban, ScrollText, X, ExternalLink, Smartphone, Repeat, Bell
} from 'lucide-react';
import { getRoleBadgeClass, getRoleLabel, DialogHost } from '../components/member/shared';
import DashboardTab from '../components/member/DashboardTab';
import NewsFeedTab from '../components/member/NewsFeedTab';
import CoursesTab from '../components/member/CoursesTab';
import CalendarTab from '../components/member/CalendarTab';
import TreeTab from '../components/member/TreeTab';
import DownlineTab from '../components/member/DownlineTab';
import KanbanTab from '../components/member/KanbanTab';
import ChangelogTab from '../components/member/ChangelogTab';
import ProfileTab from '../components/member/ProfileTab';
import UsersTab from '../components/member/UsersTab';
import LogsTab from '../components/member/LogsTab';
import RotatorTab from '../components/member/RotatorTab';
import ChatSystem from '../components/member/chat/ChatSystem';
import './Mitgliederbereich.css';

export default function Mitgliederbereich() {
  const { user, logout, isAdmin, isCoach, isAdminOrCoach, getAuthHeader, API_URL, refreshUser } = useAuth();
  const isUser = user?.role === 'user';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('news');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [calendarFocus, setCalendarFocus] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Shared data
  const [pending, setPending] = useState([]);
  const [downline, setDownline] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [rotatorLeads, setRotatorLeads] = useState([]);
  const [tree, setTree] = useState(null);
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [kanbanTasks, setKanbanTasks] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [newsFocus, setNewsFocus] = useState(null);
  const [eventRotIdx, setEventRotIdx] = useState(0);

  const authHeaders = getAuthHeader();
  const handleLogout = () => { logout(); navigate('/login'); };

  const initialLoadDoneRef = useRef(false);
  const fetchAll = useCallback(async () => {
    if (!user) return;
    if (!initialLoadDoneRef.current) setLoadingData(true);
    try {
      const reqs = [
        axios.get(`${API_URL}/api/community/sponsor/pending`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/community/sponsor/downline`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/community/events/upcoming`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/community/news`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/community/courses`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/community/categories`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/community/projects`, { headers: authHeaders }),
      ];
      if (isAdminOrCoach) {
        reqs.push(axios.get(`${API_URL}/api/community/admin/users`, { headers: authHeaders }));
        reqs.push(axios.get(`${API_URL}/api/community/admin/logs`, { headers: authHeaders }));
        reqs.push(axios.get(`${API_URL}/api/community/rotator/logs`, { headers: authHeaders }));
      }
      const results = await Promise.allSettled(reqs);
      if (results[0].status === 'fulfilled') setPending(results[0].value.data.pending || []);
      if (results[1].status === 'fulfilled') setDownline(results[1].value.data.downline || []);
      if (results[2].status === 'fulfilled') setUpcomingEvents(results[2].value.data.events || []);
      if (results[3].status === 'fulfilled') setNews(results[3].value.data.posts || []);
      if (results[4].status === 'fulfilled') setCourses(results[4].value.data.courses || []);
      if (results[5].status === 'fulfilled') setCategories(results[5].value.data.categories || []);
      if (results[6].status === 'fulfilled') setProjects(results[6].value.data.projects || []);
      if (isAdminOrCoach) {
        if (results[7]?.status === 'fulfilled') setAllUsers(results[7].value.data.users || []);
        if (results[8]?.status === 'fulfilled') { setLogs(results[8].value.data.logs || []); setLogsTotal(results[8].value.data.total || 0); }
        if (results[9]?.status === 'fulfilled') setRotatorLeads(results[9].value.data.leads || []);
      }
    } catch (e) { console.error(e); }
    initialLoadDoneRef.current = true;
    setLoadingData(false);
  }, [user, API_URL, authHeaders, isAdminOrCoach]);

  useEffect(() => { fetchAll(); }, []);

  // Refresh data when app returns to foreground (PWA/mobile)
  const lastHiddenRef = useRef(0);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenRef.current = Date.now();
      } else if (lastHiddenRef.current && Date.now() - lastHiddenRef.current > 60000) {
        fetchAll();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchAll]);

  const fetchTree = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/tree`, { headers: authHeaders });
      setTree(resp.data.tree);
    } catch (e) { console.error(e); }
  };

  const fetchEvents = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/events`, { headers: authHeaders });
      setEvents(resp.data.events || []);
      const upResp = await axios.get(`${API_URL}/api/community/events/upcoming`, { headers: authHeaders });
      setUpcomingEvents(upResp.data.events || []);
    } catch (e) { console.error(e); }
  };

  const fetchKanban = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/kanban/tasks`, { headers: authHeaders });
      setKanbanTasks(resp.data.tasks || []);
    } catch (e) { console.error(e); }
  };

  const fetchChangelog = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/changelog`, { headers: authHeaders });
      setChangelog(resp.data.entries || []);
    } catch (e) { console.error(e); }
  };

  const fetchCategories = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/categories`, { headers: authHeaders });
      setCategories(resp.data.categories || []);
    } catch (e) { console.error(e); }
  };

  // In-App Notifications (bell) + browser push
  const prevNotifIdRef = useRef(null);
  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/community/notifications`, { headers: getAuthHeader() });
      const items = resp.data.notifications || [];
      setNotifications(items);
      setNotifUnread(resp.data.unread || 0);
      const newest = items[0];
      if (newest && !newest.read && prevNotifIdRef.current !== null && prevNotifIdRef.current !== newest.id) {
        if ('Notification' in window && Notification.permission === 'granted') {
          try { new Notification('Die Freiheitsmacher', { body: newest.text, icon: '/icon-192.png', tag: `notif-${newest.id}` }); } catch (err) { /* noop */ }
        }
      }
      if (items.length > 0) prevNotifIdRef.current = items[0].id;
      else prevNotifIdRef.current = 'none';
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    if (!user) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    fetchNotifications();
    const t = setInterval(fetchNotifications, 60000);
    return () => clearInterval(t);
  }, [user, fetchNotifications]);

  const openNotifPanel = () => {
    const opening = !showNotifPanel;
    setShowNotifPanel(opening);
    if (opening && notifUnread > 0) {
      axios.post(`${API_URL}/api/community/notifications/mark-read`, {}, { headers: getAuthHeader() }).catch(() => {});
      setNotifUnread(0);
    }
  };

  const openNotification = (n) => {
    setShowNotifPanel(false);
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    if (n.post_id) {
      setNewsFocus({ id: n.post_id, type: n.type, ts: Date.now() });
      handleTabChange('news');
    }
  };

  const relTime = (d) => {
    const s = (Date.now() - new Date(d).getTime()) / 1000;
    if (s < 60) return 'gerade eben';
    if (s < 3600) return `vor ${Math.floor(s / 60)} Min`;
    if (s < 86400) return `vor ${Math.floor(s / 3600)} Std`;
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  // Countdown helper with same-day rotation
  const getVisibleUpcoming = () => {
    if (!upcomingEvents.length) return [];
    const role = user?.role || 'user';
    const now = new Date();
    return upcomingEvents.filter(ev => {
      const vt = ev.visible_to || ['user', 'sponsor', 'coach', 'admin'];
      if (!vt.includes(role)) return false;
      if (ev.event_time_end) {
        const endStr = `${ev.event_date}T${ev.event_time_end}`;
        if (new Date(endStr) < now) return false;
      }
      return true;
    });
  };

  const getCountdown = () => {
    const visible = getVisibleUpcoming();
    if (!visible.length) return null;
    const sameDay = visible.filter(ev => ev.event_date === visible[0].event_date);
    const idx = eventRotIdx % sameDay.length;
    const ev = sameDay[idx];
    const now = new Date();
    const dateStr = ev.event_time ? `${ev.event_date}T${ev.event_time}` : `${ev.event_date}T00:00`;
    const target = new Date(dateStr);
    const diff = target - now;
    if (diff <= 0) return { event: ev, text: 'Jetzt!', index: idx, total: sameDay.length };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    let text = '';
    if (days > 0) text += `${days}T `;
    text += `${hours}h ${mins}m`;
    return { event: ev, text, index: idx, total: sameDay.length };
  };

  // Rotate through same-day events every 8s
  useEffect(() => {
    const visible = getVisibleUpcoming();
    if (!visible.length) return;
    const sameDay = visible.filter(ev => ev.event_date === visible[0].event_date);
    if (sameDay.length < 2) return;
    const t = setInterval(() => setEventRotIdx(i => i + 1), 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [upcomingEvents]);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.Capacitor?.isNativePlatform();

  const tabs = [
    ...(isAdminOrCoach ? [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }] : []),
    { id: 'news', label: 'News', icon: Newspaper },
    { id: 'courses', label: 'Projekte', icon: BookOpen },
    { id: 'calendar', label: 'Kalender', icon: Calendar },
    ...(!isUser ? [{ id: 'tree', label: 'Baum', icon: GitBranch }] : []),
    ...(!isUser ? [{ id: 'sponsor', label: 'Downline', icon: Users }] : []),
    { id: 'profil', label: 'Profil', icon: UserCircle },
    ...(!isStandalone ? [{ id: 'getapp', label: 'App erhalten', icon: Smartphone }] : []),
    ...(isAdminOrCoach ? [
      { id: 'kanban', label: 'Kanban', icon: Kanban },
      { id: 'changelog', label: 'Change-Log', icon: ScrollText },
      { id: 'users', label: 'Benutzer', icon: Shield },
      { id: 'logs', label: 'Logbuch', icon: ClipboardList },
      { id: 'rotator', label: 'Rotator', icon: Repeat },
    ] : []),
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchTerm('');
    if (tabId === 'tree' && !tree) fetchTree();
    if (tabId === 'calendar') fetchEvents();
    if (tabId === 'kanban') fetchKanban();
    if (tabId === 'changelog') fetchChangelog();
  };

  const countdown = getCountdown();

  return (
    <div className={`member-page ${activeTab === 'news' ? 'news-theme-dark' : ''}`} data-testid="mitgliederbereich-page">
      <div className="member-sticky-header">
        {countdown && (
          <div
            className="smart-header"
            data-testid="smart-header"
            role="button"
            tabIndex={0}
            title="Zum Kalender-Eintrag"
            onClick={() => {
              setCalendarFocus({ date: countdown.event.event_date, id: countdown.event.id, ts: Date.now() });
              handleTabChange('calendar');
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setCalendarFocus({ date: countdown.event.event_date, id: countdown.event.id, ts: Date.now() }); handleTabChange('calendar'); } }}
          >
            <div className="smart-header-inner" key={countdown.event.id}>
              <Calendar size={16} />
              <span><strong>{countdown.event.title}</strong></span>
              <span className="countdown-badge">{countdown.text}</span>
              {countdown.total > 1 && <span className="smart-header-counter" data-testid="smart-header-counter">{countdown.index + 1}/{countdown.total}</span>}
              {countdown.event.meeting_link && (
                <a href={countdown.event.meeting_link} target="_blank" rel="noreferrer" className="meeting-link" onClick={(e) => e.stopPropagation()}>
                  <ExternalLink size={14} /> Teilnehmen
                </a>
              )}
            </div>
          </div>
        )}

        <div className="member-topbar">
          <div className="topbar-content">
            <div className="topbar-left">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="btn-menu-trigger" data-testid="menu-trigger">
                <div className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
                  <span /><span /><span />
                </div>
              </button>
              <Link to="/" className="topbar-logo" data-testid="logo-home-link">
                <img src="https://customer-assets.emergentagent.com/job_e5c77d79-ca13-402d-b554-25dcc110651b/artifacts/mj5brxa4_Logo%20Freiheitsmacher%20transparent.png" alt="Die Freiheitsmacher" className="topbar-logo-img" />
              </Link>
              <span className="topbar-active-tab">{tabs.find(t => t.id === activeTab)?.label || ''}</span>
            </div>
            <div className="topbar-right">
              <div className="notif-bell-wrap">
                <button className="notif-bell" onClick={openNotifPanel} title="Benachrichtigungen" data-testid="notif-bell">
                  <Bell size={18} />
                  {notifUnread > 0 && <span className="notif-badge" data-testid="notif-badge">{notifUnread > 9 ? '9+' : notifUnread}</span>}
                </button>
                {showNotifPanel && (
                  <>
                    <div className="notif-backdrop" onClick={() => setShowNotifPanel(false)} />
                    <div className="notif-panel" data-testid="notif-panel">
                      <div className="notif-panel-head">
                        <h4><Bell size={14} /> Benachrichtigungen</h4>
                        <button className="notif-panel-close" onClick={() => setShowNotifPanel(false)} data-testid="notif-panel-close"><X size={14} /></button>
                      </div>
                      {notifications.length === 0 ? (
                        <p className="notif-empty" data-testid="notif-empty">Keine Benachrichtigungen.</p>
                      ) : (
                        <div className="notif-list">
                          {notifications.map(n => (
                            <button key={n.id} className={`notif-item ${n.read ? '' : 'unread'}`} onClick={() => openNotification(n)} data-testid={`notif-item-${n.id}`}>
                              <span className="notif-icon">{n.type === 'post_comment' ? '💬' : '❤️'}</span>
                              <span className="notif-body">
                                <span className="notif-text">{n.text}</span>
                                <span className="notif-time">{relTime(n.created_at)}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {user && (
                <button type="button" className="topbar-user topbar-user-btn" onClick={() => handleTabChange('profil')} title="Mein Profil öffnen" data-testid="current-user-info">
                  {user.profile_image && <img src={`${API_URL}${user.profile_image}`} alt="" className="topbar-avatar" />}
                  {user.first_name} {user.last_name}
                  <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>{getRoleLabel(user.role)}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="offcanvas-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="offcanvas-menu" onClick={e => e.stopPropagation()} data-testid="nav-menu">
            <div className="offcanvas-header">
              <h3>Navigation</h3>
              <button className="offcanvas-close" onClick={() => setMobileMenuOpen(false)}><X size={20} /></button>
            </div>
            <div className="offcanvas-body">
              {tabs.map(tab => (
                <button key={tab.id} className={`offcanvas-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => { handleTabChange(tab.id); setMobileMenuOpen(false); }}
                  data-testid={`tab-${tab.id}`}>
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                  {tab.id === 'sponsor' && pending.length > 0 && <span className="tab-badge">{pending.length}</span>}
                </button>
              ))}
              <div className="offcanvas-divider" />
              <button className="offcanvas-item offcanvas-logout" onClick={() => { setMobileMenuOpen(false); handleLogout(); }} data-testid="logout-button">
                <LogOut size={18} />
                <span>Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="member-content">
        {loadingData ? (
          <div className="loading-state"><Loader2 size={32} className="spin" /><p>Lade Daten...</p></div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <DashboardTab user={user} pending={pending} downline={downline} courses={courses}
                upcomingEvents={upcomingEvents} news={news} projects={projects}
                allUsers={allUsers} isAdminOrCoach={isAdminOrCoach} rotatorLeads={rotatorLeads} />
            )}

            {activeTab === 'news' && (
              <NewsFeedTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                isAdminOrCoach={isAdminOrCoach} news={news} refreshNews={setNews}
                categories={categories} fetchCategories={fetchCategories}
                allUsers={allUsers} downline={downline} focusPost={newsFocus}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            )}

            {activeTab === 'courses' && (
              <CoursesTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                isAdmin={isAdmin} isAdminOrCoach={isAdminOrCoach}
                courses={courses} setCourses={setCourses}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            )}

            {activeTab === 'calendar' && (
              <CalendarTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                isAdminOrCoach={isAdminOrCoach} events={events} fetchEvents={fetchEvents} categories={categories} focusEvent={calendarFocus} />
            )}

            {activeTab === 'tree' && (
              <TreeTab tree={tree} projects={projects} />
            )}

            {activeTab === 'sponsor' && (
              <DownlineTab pending={pending} downline={downline}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            )}

            {activeTab === 'profil' && user && (
              <ProfileTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                isAdmin={isAdmin} projects={projects} refreshUser={refreshUser} />
            )}

            {activeTab === 'kanban' && isAdminOrCoach && (
              <KanbanTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                kanbanTasks={kanbanTasks} fetchKanban={fetchKanban} allUsers={allUsers} />
            )}

            {activeTab === 'changelog' && isAdminOrCoach && (
              <ChangelogTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                changelog={changelog} fetchChangelog={fetchChangelog} categories={categories}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            )}

            {activeTab === 'users' && isAdminOrCoach && (
              <UsersTab user={user} authHeaders={authHeaders} API_URL={API_URL}
                isAdmin={isAdmin} allUsers={allUsers} setAllUsers={setAllUsers}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm} projects={projects} />
            )}

            {activeTab === 'logs' && isAdminOrCoach && (
              <LogsTab logs={logs} setLogs={setLogs} logsTotal={logsTotal} setLogsTotal={setLogsTotal} searchTerm={searchTerm} setSearchTerm={setSearchTerm} API_URL={API_URL} authHeaders={authHeaders} />
            )}

            {activeTab === 'rotator' && isAdminOrCoach && (
              <RotatorTab authHeaders={authHeaders} API_URL={API_URL}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            )}

            {activeTab === 'getapp' && (
              <div className="tab-content" data-testid="getapp-content">
                <div className="getapp-page">
                  <div className="getapp-hero">
                    <Smartphone size={48} className="getapp-icon" />
                    <h2>Die Freiheitsmacher App</h2>
                    <p>Greife jederzeit und überall auf deine Community zu - direkt von deinem Smartphone.</p>
                  </div>

                  <div className="getapp-options">
                    <div className="getapp-card">
                      <h3>Jetzt installieren</h3>
                      <p>Installiere die Web-App direkt auf deinem Gerät - kein App Store nötig!</p>
                      <div className="getapp-steps">
                        <div className="getapp-step">
                          <span className="getapp-step-num">1</span>
                          <div>
                            <strong>Android (Chrome)</strong>
                            <p>Tippe auf das Menü (⋮) oben rechts und wähle Installieren und verknüpfen</p>
                          </div>
                        </div>
                        <div className="getapp-step">
                          <span className="getapp-step-num">2</span>
                          <div>
                            <strong>iPhone / iPad (Safari)</strong>
                            <p>Tippe auf das Teilen-Symbol (↑) unten und wähle Zum Home-Bildschirm</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="getapp-card getapp-stores">
                      <h3>Bald verfügbar</h3>
                      <p>Die native App kommt bald in die Stores!</p>
                      <div className="getapp-store-badges">
                        <div className="getapp-store-badge coming-soon">
                          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M17.523 2H6.477L2 6.477v11.046L6.477 22h11.046L22 17.523V6.477L17.523 2zM12 17.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5zm1-4h-2V7h2v6.5z"/></svg>
                          <div>
                            <small>Demnächst im</small>
                            <strong>Google Play Store</strong>
                          </div>
                        </div>
                        <div className="getapp-store-badge coming-soon">
                          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                          <div>
                            <small>Demnächst im</small>
                            <strong>Apple App Store</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="getapp-features">
                    <h3>Was die App bietet</h3>
                    <div className="getapp-feature-grid">
                      <div className="getapp-feature"><span>💬</span><strong>Chat</strong><p>Nachrichten in Echtzeit</p></div>
                      <div className="getapp-feature"><span>📰</span><strong>News</strong><p>Immer auf dem Laufenden</p></div>
                      <div className="getapp-feature"><span>📅</span><strong>Kalender</strong><p>Alle Events im Blick</p></div>
                      <div className="getapp-feature"><span>🎓</span><strong>Projekte</strong><p>Lernen unterwegs</p></div>
                      <div className="getapp-feature"><span>🔔</span><strong>Benachrichtigungen</strong><p>Nichts verpassen</p></div>
                      <div className="getapp-feature"><span>⚡</span><strong>Schnellzugriff</strong><p>Ein Tipp genügt</p></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Chat System - Overlay/Sidebar */}
      <ChatSystem user={user} authHeaders={authHeaders} API_URL={API_URL}
        isAdminOrCoach={isAdminOrCoach}
        isSponsorOrAbove={user?.role !== 'user'} />
      <DialogHost />
    </div>
  );
}
